// #region imports

// --- Angular
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray  } from '@angular/cdk/drag-drop';

// --- 3rd Party
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'oidc-client';

// --- HS Code
import {
  GroupModel,
  RespondentStatuses,
  Constants,
  SurveySummaryModel,
  RespondentModel,
  RespondentEmailValidationModel,
  LanguageModel,
} from '../../../models';
import { AppService } from '../../../core/services';
import { ManageRespondentDropdownStates } from '../../../models/shared/manage-respondent-dropdown-states';
// #endregion

@Component({
  selector: 'hs-manage-respondents',
  templateUrl: './manage-respondents.component.html',
  styleUrls: ['./manage-respondents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageRespondentsComponent implements OnInit, OnChanges {
  @Input() participantGroups: GroupModel[] | null = null;
  @Input() selectedSurvey: SurveySummaryModel | null = null;
  @Input() validatedRespondent: RespondentEmailValidationModel | null = null;
  @Input() identity: User | null = null;
  @Input() languages: LanguageModel[] | null = null;

  @Output() groupsUpdated = new EventEmitter<GroupModel[]>();
  @Output() remindRespondentClick = new EventEmitter<RespondentModel>();
  @Output() dismissModalClick = new EventEmitter();
  @Output() saveClick = new EventEmitter<GroupModel[]>();
  @Output() emailAddressChanged = new EventEmitter<
    RespondentEmailValidationModel
  >();
  @Output() groupAdded = new EventEmitter<string>();
  @Output() groupDeleted = new EventEmitter<GroupModel>();
  @Output() resetValidatedRespondent = new EventEmitter();

  groups: GroupModel[] = new Array<GroupModel>();
  charsRemaining = 0;

  canSave = false;
  isDragging = false;
  canAddGroups = false;

  viewMode: string;
  readonly viewModeManage = Constants.VIEW_MODE_MANAGE;
  readonly viewModeAdd = Constants.VIEW_MODE_ADD;

  maximumNumberOfGroupNameCharacters = Constants.GROUP_NAME_MAXIMUM_LENGTH;
  participantId: string | undefined;
  respondentStatuses = RespondentStatuses;
  selectedGroupId: string | null = null;
  numberOfRespondentsPerGroup = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;

  manageRespondentDropdownState = ManageRespondentDropdownStates.initial;
  manageRespondentDropdownStates = ManageRespondentDropdownStates;

  private _maximumNumberOfRespondents: number =
    Constants.MAXIMUM_NUMBER_OF_RESPONDENT_GROUPS * Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;
  maximumNumberOfGroups: number = Constants.MAXIMUM_NUMBER_OF_RESPONDENT_GROUPS;
  isGroupNameEditMode: boolean = false;

  constructor(
    private appService: AppService,
    private translateService: TranslateService,
  ) {
    this.viewMode = Constants.VIEW_MODE_MANAGE;
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes['selectedSurvey'] &&
      changes['selectedSurvey'].currentValue
    ) {
      this.participantId = this.selectedSurvey?.id;
      this._maximumNumberOfRespondents = this.selectedSurvey?.numberOfRespondents ?? 0;
      this.maximumNumberOfGroups = this._maximumNumberOfRespondents / 3; // ToDo: Watch out - we are going to be implementing open packs and this will be a problem
      this._setCanAddGroups();
    }
    if (
      changes &&
      changes['participantGroups'] &&
      changes['participantGroups'].currentValue
    ) {
      if (this.participantGroups) {
        this.groups = _.cloneDeep(this.participantGroups);
        for (const group of this.groups) {
           group.isValid = true;
           group.isNew = false;
           group.isDirty = false;
           group.respondents = group.respondents;
        }
      }

      this._setCanAddGroups();
    }
  }

  onUpdate(): void {}

  onGroupNameChanged(e: any): void {}

  onGroupNameBlur(e: any): void {}

  _padGroups(): void {
    if (!this.groups || !this._maximumNumberOfRespondents) {
      return;
    }

    const maximumNumberOfGroups =
      this._maximumNumberOfRespondents /
      Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;

    if (this.groups.length < maximumNumberOfGroups) {
      for (let i = 0; i < maximumNumberOfGroups - this.groups.length; i++) {
        this.groups.push(this._createEmptyGroup(this.groups.length + 1));
      }
    }

    this._evaluateCanSave();
    this.groupsUpdated.emit(this.groups);
  }

  onGroupUpdated(group: GroupModel): void {
    if (!group) {
      return;
    }

    const theGroup = this.participantGroups?.find((g) => g.id === group.id);
    const groupExist = this.groups.some((g) => g.id !== group.id && g.groupName.trim() === group.groupName);

    if (groupExist) {
      group.hasError = true;
      group.errorMessage = this.translateService.instant('participantGroupNameExists', { groupName: group.groupName });
      return;
    } else {
      group.hasError = false;
      group.errorMessage = null;
    }

    if (theGroup && group.groupName) {
      group.isDirty = theGroup.groupName !== group.groupName;
    }

    if (!group.isSet) {
      group.isNew = true;
    }

    group.isValid =
      group.groupName &&
      group.groupName.length <= Constants.GROUP_NAME_MAXIMUM_LENGTH ? true : false;

    this.groupsUpdated.emit(this.groups);
    this._evaluateCanSave();
  }

  onGroupDeleted(group: GroupModel): void {
    if (group.index > 1) {
      this.groupDeleted.emit(group);
    }
  }

  onSendReminderClick(respondent: RespondentModel): void {
    respondent.canResendEmail = false;
    const now = new Date();
    respondent.lastEmailSentDate = new Date(
      Date.UTC(
        now.getFullYear(),
        now.getMonth() + 1,
        now.getDay(),
        now.getHours(),
        now.getMinutes()
      )
    ).toString();
    this.remindRespondentClick.emit(respondent);
  }

  onDeleteRespondentClick(respondent: RespondentModel): void {
    this._removeRespondentFromGroup(respondent);
    this._evaluateCanSave();

    this.groupsUpdated.emit(this.groups);
  }

  onMoveRespondentClick(
    respondent: RespondentModel,
    targetGroup: GroupModel
  ): void {
    if (respondent.status >= RespondentStatuses.started) {
      return;
    }
    const clonedRespondent = _.cloneDeep(respondent);
    this._removeRespondentFromGroup(respondent);

    clonedRespondent.groupId = targetGroup.id;
    const unsetRespondentIndex = targetGroup.respondents.findIndex(r => !r.isSet);
    if (unsetRespondentIndex >= 0) {
      const unsetRespondent = targetGroup.respondents.find((r, i) => i === unsetRespondentIndex);
      if (unsetRespondent) {
        targetGroup.respondents[unsetRespondentIndex] = clonedRespondent;
        targetGroup.respondents[unsetRespondentIndex].groupIndex = unsetRespondent.groupIndex;
      }
    } else {
      respondent.groupIndex = targetGroup.respondents.filter(r => r.isSet).length + 1;
      targetGroup.respondents.push(clonedRespondent);
    }
    targetGroup.numberOfRespondents = targetGroup.respondents.filter(r => r.isSet).length;
    targetGroup.isDirty = true;

    this._evaluateCanSave();
    this.groupsUpdated.emit(this.groups);
  }

  getAvailableMoveGroupsList(respondent: RespondentModel | null): GroupModel[] {
    if (!respondent) {
      return this.groups.filter(
        (g) => g.respondents.filter(r => r.isSet).length < Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
      );
    }
    const availableGroups = this.groups.filter(
      (g) =>
        g.isValid && g.id.toLowerCase() !== respondent.groupId.toLowerCase() &&
        g.respondents.filter(r => r.isSet).length < Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
    );
    return availableGroups;
  }

  onDismissModalClicked() {
    this.resetValidatedRespondent.emit();
    this.dismissModalClick.emit();
    this.selectedGroupId = null;
  }

  onAddRespondentClick(respondent: RespondentModel): void {
    this.viewMode = Constants.VIEW_MODE_ADD;
    this.selectedGroupId = respondent.groupId;
  }

  onSaveClick(): void {
    const groups = this.groups.map((g) => {
      g.respondents = g.respondents.filter(r => r.isSet);
      g.numberOfRespondents = g.respondents.length;
      return g;
    });
    this.saveClick.emit(groups);
  }

  onBackButtonClicked(): void {
    this.viewMode = Constants.VIEW_MODE_MANAGE;
    this.selectedGroupId = null;
    this.resetValidatedRespondent.emit();
  }

  onEmailAddressChanged(model: RespondentEmailValidationModel): void {
    const allRespondents = _.flatten(this.groups.map((g) => g.respondents));
    const exists = allRespondents.find(
      (r) => r.emailAddress.toLowerCase() === model.emailAddress.toLowerCase()
    );

    let errorMessage = '';
    let error = false;
    if (exists) {
      error = true;
      errorMessage = this.translateService.instant('respondentErrorMessageAlreadyARespondent');
    } else {
      if (this.identity &&
        this.identity.profile &&
        model.emailAddress.toLowerCase().trim() === this.identity?.profile?.preferred_username?.toLowerCase().trim()) {
          errorMessage = this.translateService.instant('respondentErrorMessageRespondentIsUser');
          error = true;
        }
    }

    if (error) {
      this.validatedRespondent = {
        emailAddress: model.emailAddress,
        errorMessage,
        groupId: model.groupId,
        respondentGroupIndex: model.respondentGroupIndex,
        firstName: model.firstName,
        isValid: false,
        lastName: model.lastName,
        participantId: model.participantId
      } as RespondentEmailValidationModel;
      return;
    }

    model.participantId = this.selectedSurvey?.id ?? '';
    this.resetValidatedRespondent.emit();
    this.emailAddressChanged.emit(model);
  }

  onRespondentAdded(respondent: RespondentModel): void {
    const groupIndex = this.groups.findIndex(g => g.id === respondent.groupId);
    if (groupIndex >= 0) {
      this.groups[groupIndex].isDirty = true;
      const respondentIndex = this.groups[groupIndex].respondents.findIndex(r => !r.isSet);
      if (respondentIndex === -1) {
        return;
      }
      respondent.groupIndex = respondentIndex + 1;
      this.groups[groupIndex].respondents[respondentIndex] = respondent;
      this.groups[groupIndex].numberOfRespondents = this.groups[groupIndex].respondents.filter(r => r.isSet).length;

      this._evaluateCanSave();
      this.groupsUpdated.emit(this.groups);
    }

    this.viewMode = Constants.VIEW_MODE_MANAGE;
    this.selectedGroupId = null;
    this.resetValidatedRespondent.emit();
    this._setCanAddGroups();
  }

  onDrop(event: CdkDragDrop<RespondentModel[] | undefined, RespondentModel[] | undefined, any>): void {
    if (event.previousContainer !== event.container) {
      this._transferRespondents(event);
    } else {
      this._reOrderRespondents(event);
    }

    this._evaluateCanSave();
  }

  onMouseDown(): void {
    this.isDragging = true;
  }

  onMouseUp(): void {
    this.isDragging = false;
  }

  updateManageRespondentDropdownState(state: ManageRespondentDropdownStates) {
    this.manageRespondentDropdownState = state;
  }

  onManageRespondentDropdownOpenChange(event: boolean) {
    if (!event) {
      this.updateManageRespondentDropdownState(ManageRespondentDropdownStates.initial);
    }
  }

  onGroupNameControlModeUpdate(isEditMode: boolean) {
    this.isGroupNameEditMode = isEditMode;
  }

  onAddGroup(): void {
    this.groupAdded.emit(this.selectedSurvey?.id);
  }

  trackByFn(index: number, respondent: RespondentModel) {
    return respondent && respondent.id;
  }

  private _setCanAddGroups(): void {
    if (!this.groups) {
      return;
    }
    const setGroups = this.groups.filter(g => g.isSet || g.isNew);
    const hasInvalidRespondents = setGroups.some((g) => g.respondents.some(r => !r.isValid));
    this.canAddGroups =
      this.maximumNumberOfGroups > this.groups.length &&
      setGroups.length === this.groups.length && !hasInvalidRespondents;
  }

  private _createEmptyGroup(index: number): GroupModel {
    const id = this.appService.createGuid();
    return {
      id,
      groupName: '',
      index,
      participantId: this.participantId,
      isActiveGroup: true,
      isSet: false,
      numberOfRespondentsComplete: 0,
      numberOfRespondents: 0,
      isNew: true,
      hasError: false,
      errorMessage: '',
      respondents: new Array<RespondentModel>(),
    } as GroupModel;
  }

  private _createEmptyRespondentModel(
    groupId: string,
    groupIndex: number,
    isExpanded = false
  ): RespondentModel {
    return {
      emailAddress: '',
      firstName: '',
      lastName: '',
      groupId,
      groupIndex,
      isExpanded,
      percentageComplete: 0,
    } as RespondentModel;
  }

  private _removeRespondentFromGroup(respondent: RespondentModel) {
    for (const group of this.groups) {
      const respondentIndex = group.respondents.findIndex(
        (r) => r.id === respondent.id
      );

      if (respondentIndex >= 0) {
        if (group.respondents.length > Constants.NUMBER_OF_RESPONDENTS_PER_GROUP) {
          group.respondents.splice(respondentIndex, 1);
          for (let i = 0; i < group.respondents.length; i++) {
            group.respondents[i].groupIndex = i + 1;
          }
        } else {
          this._resetRespondent(group.respondents[respondentIndex]);
        }

        group.numberOfRespondents = group.respondents.filter(r => r.isSet).length;
        group.isDirty = true;
        break;
      }
    }
  }

  private _resetRespondent(respondent: RespondentModel) {
    respondent.isSet = false;
    respondent.isValid = false;
    respondent.emailAddress = '';
    respondent.avatar = '';
    respondent.canResendEmail = false;
    respondent.id = null;
    respondent.participantId = null;
    respondent.firstName = '';
    respondent.lastName = '';
    respondent.emailAddress = '';
    respondent.locale = '';
    respondent.languageId = 0;
    respondent.isValid = false;
    respondent.isSet = false;
    respondent.errorMessage = '';
    respondent.personalMessage = '';
    respondent.isValidatingEmailAddress = false;
    respondent.isSaving = false;
  }

  private _transferRespondents(event: CdkDragDrop<RespondentModel[] | undefined, RespondentModel[] | undefined, any>) {
    if (!event.container || !event.container.data) return;
    if (!event.previousContainer || !event.previousContainer.data) return;

    const previousGroup = this.groups.find(g => g.id.toLowerCase() === event.previousContainer.id.toLowerCase());
    const currentGroup = this.groups.find(g => g.id.toLowerCase() === event.container.id.toLowerCase());
    const item = event.previousContainer.data[event.previousIndex];

    if (previousGroup && currentGroup) {
      let movedRespondent = previousGroup.respondents.find(r => r.id && r.id.toLowerCase() === item?.id?.toLowerCase());
      if (!movedRespondent) {
        movedRespondent = previousGroup.respondents.find(r => r.groupIndex === item.groupIndex);
      }
      if (!movedRespondent) {
        return;
      }
      this.onMoveRespondentClick(movedRespondent, currentGroup);
      currentGroup.isDirty = true;
      previousGroup.isDirty = true;
      currentGroup.numberOfRespondents = currentGroup.respondents.filter(r => r.isSet).length;
      previousGroup.numberOfRespondents = previousGroup.respondents.filter(r => r.isSet).length;
      for (let i = 0; i < currentGroup.respondents.length; i++) {
        currentGroup.respondents[i].groupIndex = i + 1;
      }
      for (let i = 0; i < previousGroup.respondents.length; i++) {
        previousGroup.respondents[i].groupIndex = i + 1;
      }
    }
    this.groups = _.cloneDeep(this.groups);
  }

  private _reOrderRespondents(event: CdkDragDrop<RespondentModel[] | undefined, RespondentModel[] | undefined, any>) {
    if (!event.container || !event.container.data) return;
    if (!event.previousContainer || !event.previousContainer.data) return;

    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const item = event.container.data[event.currentIndex];
    const theGroup = this.groups.find(g => g.id.toLowerCase() === item.groupId.toLowerCase());
    if (!theGroup) {
      return;
    }
    theGroup.isDirty = true;
    moveItemInArray(
      event.container.data,
       event.previousIndex,
       event.currentIndex
    );
    for (let i = 0; i < theGroup.respondents.length; i++) {
      theGroup.respondents[i].groupIndex = i + 1;
    }
    this.groups = _.cloneDeep(this.groups);
  }

  private _evaluateCanSave(): void {
    if (!this.groups) {
      this.canSave = false;
    }

    const isNew = this.groups.filter((g) => g.isNew && g.isValid).length > 0;
    const isDirty = this.groups.filter((g) => g.isDirty).length > 0;
    const hasErrors = this.groups.filter((g) => g.hasError).length > 0;

    this.canSave = (
      !hasErrors &&
      (isNew || isDirty) &&
      this.groups[0].respondents &&
      this.groups[0].respondents.filter(r => r.isSet).length ===
        Constants.NUMBER_OF_RESPONDENTS_PER_GROUP
    );
  }
}
